import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { OrderStatus } from '@utility/constants/common';
import {
  singleShipmentSchema,
  type ManualShipItem,
  type ManualShipPayload,
  type ManualShipProviderOption,
  type SingleShipmentValues,
} from '../../lib/manual-ship';
import {
  ComboBoxField,
  SelectField,
  TextInputField,
  toSelectOptions,
  type LegacyOption,
} from '../fulfillment/fields';
import { ManualShipActions } from './ManualShipActions';

export type SingleShipmentFormProps = {
  items: ManualShipItem[];
  providerOptions: ManualShipProviderOption[];
  providersLoading?: boolean;
  carriers: LegacyOption[];
  carriersLoading?: boolean;
  defaultProviderId?: string;
  isSaving?: boolean;
  onSubmit: (payload: ManualShipPayload) => void;
  onCancel: () => void;
};

const SHIP_FROM_HINT =
  'To purchase a label you need at least one warehouse with inventory. To bypass this, turn on “Fulfill when out of stock” on the warehouse.';

/** One tracking number covering everything still pending on the order. */
export function SingleShipmentForm({
  items,
  providerOptions,
  providersLoading,
  carriers,
  carriersLoading,
  defaultProviderId = '',
  isSaving,
  onSubmit,
  onCancel,
}: SingleShipmentFormProps) {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
  } = useForm<SingleShipmentValues>({
    mode: 'all',
    resolver: zodResolver(singleShipmentSchema),
    defaultValues: {
      fulfillment_provider_id: defaultProviderId,
      provider: '',
      tracking_number: '',
    },
  });

  /* The warehouse follows the card's own picker, without touching what was typed. */
  useEffect(() => {
    setValue('fulfillment_provider_id', defaultProviderId);
  }, [defaultProviderId, setValue]);

  const pendingItems = useMemo(
    () =>
      items.filter(
        item => item.content?.item_status === OrderStatus.PendingShipment,
      ),
    [items],
  );

  const submit = (values: SingleShipmentValues) =>
    onSubmit({
      fulfillment_providers: [
        {
          fulfillment_provider_id: values.fulfillment_provider_id,
          items: pendingItems.map(item => ({ item_id: item._id })),
          package: '',
          provider: values.provider,
          service: '',
          tracking_id: values.tracking_number,
        },
      ],
    });

  return (
    <form
      noValidate
      onSubmit={handleSubmit(submit)}
      className="sk:flex sk:flex-col sk:gap-4"
    >
      <SelectField
        control={control}
        name="fulfillment_provider_id"
        label="Ship from"
        hint={SHIP_FROM_HINT}
        placeholder={providersLoading ? 'Loading warehouses' : 'Ship from'}
        options={providerOptions.map(option => ({
          value: option.value,
          label: option.text,
        }))}
        error={errors.fulfillment_provider_id?.message}
      />
      <ComboBoxField
        control={control}
        name="provider"
        label="Shipping carrier"
        placeholder="Select or type a carrier"
        options={toSelectOptions(carriers)}
        isDisabled={carriersLoading}
        error={errors.provider?.message}
      />
      <TextInputField
        control={control}
        name="tracking_number"
        label="Tracking number"
        placeholder="Enter tracking number"
        error={errors.tracking_number?.message}
      />

      <ManualShipActions
        isDisabled={!isValid}
        isSaving={isSaving}
        onCancel={onCancel}
      />
    </form>
  );
}
